import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import usersApi from "@/api/usersApi";
import companiesApi from "@/api/companiesApi";
import ApiError from "@/model/ApiError";
import UserRole from "@/model/UserRole";
import CompanySummaryResponse from "@/model/CompanySummaryResponse";
import roleHomePath from "@/routing/roleHomePath";
import "@/pages/CreateUserPage/CreateUserPage.scss";

const COMPANIES_PAGE_SIZE = 100;

function roleOptionsFor(callerRole: UserRole): UserRole[] {
  return callerRole === "ADMIN" ? ["HOST", "USER", "ADMIN"] : ["HOST"];
}

function CreateUserPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const navigate = useNavigate();

  const roleOptions = session ? roleOptionsFor(session.role) : [];

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // HOST is valid for every caller; the session (and so roleOptions) may not be loaded on first render.
  const [role, setRole] = useState<UserRole>("HOST");
  const [companyId, setCompanyId] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [companies, setCompanies] = useState<CompanySummaryResponse[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [companiesError, setCompaniesError] = useState(false);

  // Only hosts belong to a company; guests (USER) and admins never do.
  const showCompanyId = session ? session.role === "ADMIN" && role === "HOST" : false;

  useEffect(() => {
    if (!session || !showCompanyId) {
      return;
    }

    let cancelled = false;
    setIsLoadingCompanies(true);
    setCompaniesError(false);

    companiesApi
      .listCompanies(session.accessToken, 0, COMPANIES_PAGE_SIZE)
      .then((result) => {
        if (!cancelled) {
          setCompanies(result.content ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCompaniesError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingCompanies(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, showCompanyId]);

  if (!session) {
    return null;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await usersApi.createUser(session.accessToken, {
        firstName,
        lastName,
        email,
        password,
        role,
        companyId: showCompanyId ? companyId.trim() || undefined : undefined,
      });
      navigate(roleHomePath(session.role), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t("users.create.emailInUse"));
      } else if (err instanceof ApiError && err.status === 403) {
        setError(t("users.create.forbidden"));
      } else if (err instanceof ApiError && err.status === 400) {
        setError(t("users.create.validationError"));
      } else {
        setError(t("users.create.genericError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-user-page">
      <form className="create-user-page__form" onSubmit={handleSubmit}>
        <h1>{t("users.create.title")}</h1>

        <label htmlFor="firstName">{t("users.create.firstNameLabel")}</label>
        <input
          id="firstName"
          type="text"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          required
        />

        <label htmlFor="lastName">{t("users.create.lastNameLabel")}</label>
        <input
          id="lastName"
          type="text"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          required
        />

        <label htmlFor="email">{t("users.create.emailLabel")}</label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />

        <label htmlFor="password">{t("users.create.passwordLabel")}</label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          minLength={8}
          required
        />

        <label htmlFor="role">{t("users.create.roleLabel")}</label>
        <select
          id="role"
          value={role}
          onChange={(event) => setRole(event.target.value as UserRole)}
        >
          {roleOptions.map((option) => (
            <option key={option} value={option}>
              {t(`users.create.roles.${option}`)}
            </option>
          ))}
        </select>

        {showCompanyId && (
          <>
            <label htmlFor="companyId">
              {t("users.create.companyIdLabel")}
            </label>
            <select
              id="companyId"
              value={companyId}
              onChange={(event) => setCompanyId(event.target.value)}
              disabled={isLoadingCompanies || companiesError}
              required
            >
              <option value="" disabled>
                {t("users.create.companyIdPlaceholder")}
              </option>
              {companies
                .filter(
                  (company): company is CompanySummaryResponse & { id: string } =>
                    Boolean(company.id)
                )
                .map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name ?? company.id}
                  </option>
                ))}
            </select>
            {isLoadingCompanies && (
              <p className="create-user-page__help">
                {t("users.create.companiesLoading")}
              </p>
            )}
            {companiesError && (
              <p className="create-user-page__error">
                {t("users.create.companiesLoadError")}
              </p>
            )}
            {!isLoadingCompanies && !companiesError && (
              <p className="create-user-page__help">
                {t("users.create.companyIdHelp")}
              </p>
            )}
          </>
        )}

        {error && <p className="create-user-page__error">{error}</p>}

        <div className="create-user-page__actions">
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t("users.create.submitting")
              : t("users.create.submit")}
          </button>
          <Link to={roleHomePath(session.role)}>
            {t("users.create.cancel")}
          </Link>
        </div>
      </form>
    </div>
  );
}

export default CreateUserPage;
